/**
 * Project Detail Panel Component
 * Displays full project details with workflow, info, contact, notes, and SKUs
 */

import { useState } from 'react';
import { Edit2, Archive, ArchiveRestore, Calendar, Building2, Hash, DollarSign, Bell, BellOff, Clock, CheckCircle2, CircleDashed, MapPin, Truck, Package, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { WorkflowStepper } from './WorkflowStepper';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import { ProjectTypeBadge } from './ProjectTypeBadge';
import { ProjectContactSection } from './ProjectContactSection';
import { ProjectNotesSection } from './ProjectNotesSection';
import { ProjectSkusSection } from './ProjectSkusSection';
import { ProjectTasksSection } from './ProjectTasksSection';
import { ProjectActivityLog } from './ProjectActivityLog';
import { useTasksByProject, useTasksByQuote } from '@/features/operations-tasks';
import { QuoteViewModal, useQuoteByNumber } from '@/features/quotes';
import { getQuoteHtmlUrlV4 } from '@/features/quotes/api';
import { useCanadaPostTracking, TrackingTimelineSheet, DeliveryStatusBadge } from '@/features/shippings';
import { getCanadaPostTrackingUrl } from '@/features/shippings';
import type { CanadaPostTrackingResult } from '@/features/shippings';
import type { Project, WorkflowStep } from '../types';

interface ProjectDetailPanelProps {
  project?: Project;
  isLoading?: boolean;
  onEdit: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onCompleteStep: (stepKey: string) => void;
  onUncompleteStep?: (stepKey: string) => void;
  isArchiving?: boolean;
  isUnarchiving?: boolean;
  isCompletingStep?: boolean;
  canEditProjects?: boolean;
  canDeleteProjects?: boolean;
}

export function ProjectDetailPanel({
  project,
  isLoading,
  onEdit,
  onArchive,
  onUnarchive,
  onCompleteStep,
  onUncompleteStep,
  isArchiving,
  isUnarchiving,
  isCompletingStep,
  canEditProjects = true,
  canDeleteProjects = false,
}: ProjectDetailPanelProps) {
  const quoteId = project?.quote_id;
  const projectTasks = useTasksByProject(
    quoteId ? undefined : project?.id,
    { pageLength: 50 }
  );
  const quoteTasks = useTasksByQuote(
    quoteId,
    { pageLength: 50 }
  );
  const tasksData = quoteId ? quoteTasks.data : projectTasks.data;
  const isLoadingTasks = quoteId ? quoteTasks.isLoading : projectTasks.isLoading;

  // Canada Post tracking
  const isCanadaPostCourier = project?.courier?.toLowerCase() === 'canada post';
  const trackingNumber = project?.tracking_number?.trim() || '';
  const { data: trackingData, isLoading: isLoadingTracking } = useCanadaPostTracking(
    trackingNumber ? [trackingNumber] : [],
    isCanadaPostCourier && !!trackingNumber
  );
  const trackingResult: CanadaPostTrackingResult | null = trackingData?.[0] ?? null;
  const [isTrackingSheetOpen, setIsTrackingSheetOpen] = useState(false);

  // Quote modal state
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const { data: quoteData } = useQuoteByNumber(
    isQuoteModalOpen ? project?.quote_number : undefined
  );
  const quoteTitle = project?.quote_title || quoteData?.name || quoteData?.projectName || project?.project_name;

  const handleViewQuotePdf = (quoteId: string) => {
    window.open(getQuoteHtmlUrlV4(quoteId), '_blank');
  };

  if (isLoading) {
    return <ProjectDetailPanelSkeleton />;
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Select a project to view details
      </div>
    );
  }

  const formatCurrency = (amount: number | undefined, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount ?? 0);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    // Convert 24h format to 12h format
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  /**
   * Returns an external tracking URL for non-Canada-Post couriers, or null.
   * Canada Post is handled separately with live API tracking.
   */
  const getCourierTrackingUrl = (courier: string | undefined, tn: string): string | null => {
    if (!courier || !tn) return null;
    const c = courier.toLowerCase();
    if (c === 'planet courier' || c === 'planète courrier')
      return `https://www.planetecourrier.com/en/parcel-tracking.html?r=${encodeURIComponent(tn)}`;
    if (c === 'fedex') return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`;
    if (c === 'ups') return `https://www.ups.com/track?tracknum=${encodeURIComponent(tn)}`;
    if (c === 'dhl') return `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(tn)}`;
    if (c === 'purolator') return `https://www.purolator.com/en/shipping/tracker?pins=${encodeURIComponent(tn)}`;
    return null;
  };

  const courierTrackingUrl = getCourierTrackingUrl(project?.courier, trackingNumber);

  // Find the next incomplete step
  const getNextStep = (steps: WorkflowStep[]): WorkflowStep | undefined => {
    return steps.find((step) => step.status === 'pending' || step.status === 'current');
  };

  const nextStep = getNextStep(project.milestones);

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="shrink-0 px-6 pt-6 pb-4 border-b bg-background">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {project.quote_number ? (
                <button
                  onClick={() => setIsQuoteModalOpen(true)}
                  className="text-2xl font-semibold text-left hover:text-primary hover:underline transition-colors"
                >
                  Quote #{project.quote_number}
                </button>
              ) : (
                <h2 className="text-2xl font-semibold">
                  {project.project_name || `Project ${project.project_number}`}
                </h2>
              )}
              <ProjectTypeBadge type={project.type} />
            </div>
            <p className="text-lg text-muted-foreground">{project.account_name}</p>
          </div>
          <div className="flex gap-2">
            {canEditProjects && (
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit2 className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            {canDeleteProjects && project.archived_at ? (
              <Button
                variant="outline"
                size="sm"
                disabled={isUnarchiving}
                onClick={onUnarchive}
              >
                <ArchiveRestore className="h-4 w-4 mr-1" />
                Unarchive
              </Button>
            ) : canDeleteProjects && onArchive ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isArchiving}>
                    <Archive className="h-4 w-4 mr-1" />
                    Archive
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive Project</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to archive this project? This action can be undone later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onArchive} disabled={isArchiving}>
                      Archive
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto space-y-6 p-6">

      {/* Workflow Stepper */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Workflow Progress</h3>
        <WorkflowStepper
          steps={project.milestones}
          onCompleteStep={onCompleteStep}
          onStepUncomplete={onUncompleteStep}
          isCompletingStep={isCompletingStep}
        />
        {nextStep && (
          <div className="mt-3 text-sm text-muted-foreground">
            Next step: <span className="font-medium text-foreground">{nextStep.label}</span>
          </div>
        )}
      </div>

      <Separator />

      {/* Info Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Project Info</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Hash className="h-4 w-4" />
                {project.quote_number ? 'Quote Number' : 'Project Number'}
              </div>
              <p className="font-medium">{project.quote_number || project.project_number}</p>
              <p className="text-sm text-muted-foreground">
                Quote Title: <span className="text-foreground">{quoteTitle || 'No quote title'}</span>
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="h-4 w-4" />
                Account
              </div>
              <p className="font-medium">
                {project.account_number && (
                  <span className="text-muted-foreground font-normal mr-1.5">#{project.account_number}</span>
                )}
                {project.account_name}
              </p>
            </div>
            <div className="space-y-1 col-span-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Scheduled Dates
              </div>
              {project.scheduled_dates && project.scheduled_dates.length > 0 ? (
                <div className="space-y-2">
                  {project.scheduled_dates.map((schedDate) => (
                    <div
                      key={schedDate.id}
                      className="flex items-center gap-2"
                    >
                      {schedDate.status === 'confirmed' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                      ) : (
                        <CircleDashed className="h-4 w-4 text-yellow-600 shrink-0" />
                      )}
                      <span className="font-medium">{formatDate(schedDate.date)}</span>
                      {schedDate.time && (
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Clock className="h-3 w-3" />
                          {formatTime(schedDate.time)}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        schedDate.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {schedDate.status === 'confirmed' ? 'Confirmed' : 'Proposed'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : project.scheduled_date ? (
                <p className="font-medium">{formatDate(project.scheduled_date)}</p>
              ) : (
                <p className="text-muted-foreground">No dates scheduled</p>
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                Amount
              </div>
              <p className="font-medium">{formatCurrency(project.amount, project.currency)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                Installation Address
              </div>
              {project.address || project.city ? (
                <p className="font-medium">
                  {[
                    project.address,
                    [project.city, project.state_province].filter(Boolean).join(', '),
                    project.postal_code,
                    project.country,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              ) : (
                <p className="text-muted-foreground">No address specified</p>
              )}
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Type</div>
              <ProjectTypeBadge type={project.type} />
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Status</div>
              <ProjectStatusBadge status={project.status} />
            </div>
            {project.status === 'roadblock' && project.roadblock_reason && (
              <div className="space-y-1 col-span-2">
                <div className="text-sm text-muted-foreground">Roadblock Reason</div>
                <p className="text-sm bg-red-50 text-red-800 p-2 rounded border border-red-200">
                  {project.roadblock_reason}
                </p>
              </div>
            )}
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Email Notifications</div>
              <div className="flex items-center gap-1.5">
                {project.notify_customer !== false ? (
                  <>
                    <Bell className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">Enabled</span>
                  </>
                ) : (
                  <>
                    <BellOff className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Disabled</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shipping Section - only show if any shipping data exists */}
      {(project.shipping_address || project.shipping_date || project.courier || project.tracking_number) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Shipping</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 col-span-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Truck className="h-4 w-4" />
                  Shipping Address
                </div>
                {project.shipping_address ? (
                  <p className="font-medium">{project.shipping_address}</p>
                ) : (
                  <p className="text-muted-foreground">No shipping address</p>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Shipping Date
                </div>
                <p className="font-medium">{formatDate(project.shipping_date)}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="h-4 w-4" />
                  Courier
                </div>
                {project.courier ? (
                  <p className="font-medium">{project.courier}</p>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
              <div className="space-y-1 col-span-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Hash className="h-4 w-4" />
                  Tracking Number
                </div>
                {project.tracking_number ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium font-mono text-sm">{project.tracking_number}</p>
                      {isCanadaPostCourier && (
                        <DeliveryStatusBadge
                          status={trackingResult?.events[0]?.description ?? null}
                          isLoading={isLoadingTracking}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isCanadaPostCourier && trackingNumber && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsTrackingSheetOpen(true)}
                            disabled={isLoadingTracking || !trackingResult}
                          >
                            View Full Tracking
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <a
                              href={getCanadaPostTrackingUrl(trackingNumber)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Canada Post
                            </a>
                          </Button>
                        </>
                      )}
                      {!isCanadaPostCourier && courierTrackingUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={courierTrackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Track on {project.courier}
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Canada Post Tracking Timeline Sheet */}
      {isCanadaPostCourier && trackingResult && (
        <TrackingTimelineSheet
          open={isTrackingSheetOpen}
          onOpenChange={setIsTrackingSheetOpen}
          tracking={trackingResult}
        />
      )}

      {/* Contact Section */}
      <ProjectContactSection
        contactName={project.contact_name}
        contactEmail={project.contact_email}
        contactPhone={project.contact_phone}
        accountManagerName={project.account_manager_name}
      />

      {/* Notes Section */}
      <ProjectNotesSection notes={project.notes} />

      {/* Tasks Section */}
      <ProjectTasksSection
        tasks={tasksData?.tasks || []}
        isLoading={isLoadingTasks}
      />

      {/* SKUs Section */}
      <ProjectSkusSection skus={project.skus} />

      {/* Activity Log Section */}
      <ProjectActivityLog projectId={project.id} />

      {/* Quote View Modal */}
      {project.quote_number && (
        <QuoteViewModal
          open={isQuoteModalOpen}
          onOpenChange={setIsQuoteModalOpen}
          quote={quoteData || null}
          onViewPdf={handleViewQuotePdf}
        />
      )}
      </div>
    </div>
  );
}

function ProjectDetailPanelSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-5 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      <Separator />
      <div>
        <Skeleton className="h-4 w-32 mb-3" />
        <Skeleton className="h-16 w-full" />
      </div>
      <Separator />
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-24" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}
